import { Alert, Box, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import type { IntakeServiceCatalogEntry } from '../intakeServicesCatalog';
import type { IntakeAnswers, TherapyType } from '../types';
import type { IntakeAction } from '../types';

function toggleTherapyType(selected: TherapyType[], value: TherapyType): TherapyType[] {
  return selected.includes(value)
    ? selected.filter((t) => t !== value)
    : [...selected, value];
}

interface StepTherapyTypeProps {
  answers: IntakeAnswers;
  dispatch: React.Dispatch<IntakeAction>;
  services: IntakeServiceCatalogEntry[];
  servicesLoading: boolean;
  servicesError: string | null;
  /** Therapy types to hide (e.g. services the client already has active). */
  excludedTherapyTypes?: TherapyType[];
}

export function StepTherapyType({
  answers,
  dispatch,
  services,
  servicesLoading,
  servicesError,
  excludedTherapyTypes = [],
}: StepTherapyTypeProps): JSX.Element {
  const selected = answers.therapyTypes;
  const excluded = new Set(excludedTherapyTypes);
  const availableServices = services.filter(
    (service) => !excluded.has(service.code as TherapyType),
  );

  if (servicesLoading) {
    return (
      <Box ta="center" py="xl">
        <Loader size="md" color="gold" />
      </Box>
    );
  }

  if (servicesError) {
    return (
      <Alert color="red" variant="light" radius="md" title="Unable to load services">
        {servicesError}
      </Alert>
    );
  }

  if (availableServices.length === 0) {
    return (
      <Alert color="yellow" variant="light" radius="md" title="No services available">
        {services.length > 0 && excluded.size > 0
          ? 'You already have active care for each service type we offer. Contact support if you need something else.'
          : 'There are no active services available for intake at this time. Please try again later.'}
      </Alert>
    );
  }

  return (
    <Stack gap="xs">
      <Text size="sm" c="dimmed" mb={4}>
        If you are seeking care for more than one person, please complete a separate intake form
        for each individual.
      </Text>
      {availableServices.map((service) => {
        const therapyType = service.code as TherapyType;
        const isSelected = selected.includes(therapyType);
        return (
          <Box
            key={service.id}
            component="button"
            type="button"
            onClick={() => {
              dispatch({
                type: 'SET_THERAPY_TYPES',
                payload: toggleTherapyType(selected, therapyType),
              });
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              textAlign: 'left',
              padding: '12px 16px',
              border: '1px solid',
              borderColor: isSelected ? 'var(--mantine-color-gold-5)' : 'var(--mantine-color-gray-3)',
              backgroundColor: isSelected ? 'var(--mantine-color-gold-0)' : 'var(--mantine-color-white)',
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'border-color 0.15s, background-color 0.15s',
            }}
          >
            <ThemeIcon
              size="md"
              radius="sm"
              variant={isSelected ? 'filled' : 'outline'}
              color={isSelected ? 'gold' : 'gray.3'}
              aria-hidden="true"
            >
              {isSelected && <IconCheck size={14} />}
            </ThemeIcon>
            <Text size="md" fw={500} c="dark.7">
              {service.name}
            </Text>
          </Box>
        );
      })}
    </Stack>
  );
}
